import { POOLS_MAP, PoolName, TRANSACTION_TYPES } from "../constants"
import { commify, getChainId } from "../utils"
import { useMemo, useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  useLPTokenContract,
  useMiniChefContract,
  useFerroFarmV2Contract,
  useFerroFarmBoostContract,
} from "./useContract"
import { AppState } from "../state"
import { BigNumber } from "@ethersproject/bignumber"
import { Zero } from "@ethersproject/constants"
import { useGetBoostedFarms } from "src/hooks/BoostFarming/useFarmBoostContract"
import { useCheckAndApproveTokenForTrade } from "./useCheckAndApproveTokenForTrade"
import { updateLastTransactionTimes } from "../state/application"
import { useActiveWeb3React } from "."
import { ContractReceipt } from "ethers"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { formatUnits } from "ethers/lib/utils"
import { TransactionQueueItem } from "src/providers/TransactionModalProvider"
import { useInvalidateUserData } from "./usePoolData"
import useFarmBoostHandler from "src/hooks/BoostFarming/useFarmBoostHandler"

export function useRewardsHelpers(poolName: PoolName): {
  approveAndStake: (amount: BigNumber) => Promise<ContractReceipt | void>
  unstake: (amount: BigNumber) => Promise<ContractReceipt | void>
  amountStaked: BigNumber
  isPoolIncentivized: boolean
} {
  const pool = POOLS_MAP[poolName]
  const { account, library } = useActiveWeb3React()
  const chainId = getChainId()
  const dispatch = useDispatch()
  const { addTransactions } = useTransactionModal()
  const lpTokenContract = useLPTokenContract(poolName)
  const { infiniteApproval } = useSelector((state: AppState) => state.user)
  const farmContract = useMiniChefContract()
  const farmV2Contract = useFerroFarmV2Contract()
  const farmBoostConstract = useFerroFarmBoostContract()
  const checkAndApproveTokenForTrade = useCheckAndApproveTokenForTrade()
  const { onInvalidateUserData } = useInvalidateUserData(poolName)
  const { onInvalidFarmBoostData } = useFarmBoostHandler()
  const { data: boostedPoolIds } = useGetBoostedFarms()
  const poolPid = chainId && pool ? pool.rewardPids[chainId] : null
  const [amountStaked, setAmountStaked] = useState(Zero)
  // const [amountOfSpaClaimable, setAmountOfSpaClaimable] = useState(Zero)
  const { lastTransactionTimes } = useSelector(
    (state: AppState) => state.application,
  )
  const lastStakeOrClaim =
    lastTransactionTimes[TRANSACTION_TYPES.STAKE_OR_CLAIM]

  const currentContract = useMemo(
    () => (pool?.version === 2 ? farmV2Contract : farmContract),
    [pool, farmContract, farmV2Contract],
  )

  const checkUserShare = useCallback(
    (transcations: TransactionQueueItem[]) => {
      if (
        poolPid &&
        boostedPoolIds &&
        boostedPoolIds.includes(poolPid) &&
        account &&
        farmBoostConstract
      ) {
        const updateShareTxt = async () =>
          await farmBoostConstract["updateShares(address,uint256)"](
            account,
            poolPid,
          )
        transcations.push(updateShareTxt)
      }
    },
    [account, boostedPoolIds, farmBoostConstract, poolPid],
  )

  const approveAndStake = useCallback(
    async (amount: BigNumber) => {
      if (!lpTokenContract || !account || poolPid === null || !chainId) return

      if (!currentContract) return
      const token = pool.lpToken
      await checkAndApproveTokenForTrade(
        lpTokenContract,
        currentContract.address,
        account,
        amount,
        infiniteApproval,
        BigNumber.from(1),
        {
          onTransactionError: () => {
            throw new Error("Your transaction could not be completed")
          },
        },
        token,
      )
      const depositTxt = async () =>
        await currentContract.deposit(poolPid, amount)
      const transactions = [depositTxt]
      checkUserShare(transactions)
      const summary = [
        {
          label: "Stake",
          amount: commify(formatUnits(amount, 18)),
          token: pool.lpToken,
        },
      ]
      await addTransactions(transactions, summary)
      onInvalidateUserData()
      onInvalidFarmBoostData()
      dispatch(
        updateLastTransactionTimes({
          [TRANSACTION_TYPES.STAKE_OR_CLAIM]: Date.now(),
        }),
      )
      return Promise.resolve()
    },
    [
      lpTokenContract,
      currentContract,
      account,
      poolPid,
      chainId,
      pool.lpToken,
      checkAndApproveTokenForTrade,
      infiniteApproval,
      checkUserShare,
      addTransactions,
      onInvalidateUserData,
      onInvalidFarmBoostData,
      dispatch,
    ],
  )

  const unstake = useCallback(
    async (amount: BigNumber) => {
      if (
        !lpTokenContract ||
        !currentContract ||
        !account ||
        poolPid === null ||
        !chainId
      )
        return

      const summary = [
        {
          label: "Unstake",
          amount: commify(formatUnits(amount, 18)),
          token: pool.lpToken,
        },
      ]

      const withdrawTxt = async () =>
        await currentContract.withdraw(poolPid, amount)
      const transcations = [withdrawTxt]
      checkUserShare(transcations)
      await addTransactions(transcations, summary)
      onInvalidateUserData()
      onInvalidFarmBoostData()
      dispatch(
        updateLastTransactionTimes({
          [TRANSACTION_TYPES.STAKE_OR_CLAIM]: Date.now(),
        }),
      )
      return Promise.resolve()
    },
    [
      lpTokenContract,
      currentContract,
      account,
      poolPid,
      chainId,
      pool.lpToken,
      checkUserShare,
      addTransactions,
      onInvalidateUserData,
      onInvalidFarmBoostData,
      dispatch,
    ],
  )

  useEffect(() => {
    async function fetchAmount() {
      if (
        !currentContract ||
        !account ||
        poolPid === null ||
        !library ||
        !chainId
      )
        return
      const userInfo = await currentContract
        .userInfo(poolPid, account)
        .catch(console.error)
      setAmountStaked(userInfo ? userInfo.amount : Zero)
    }
    void fetchAmount()
  }, [account, poolPid, currentContract, lastStakeOrClaim, library, chainId])

  return {
    approveAndStake,
    unstake,
    amountStaked,
    isPoolIncentivized: poolPid !== null,
  }
}
